import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  useDashboardStats,
  useDashboardUserStats,
  useDashboardClientStats,
  type TrendStat,
  type ActivityItem,
  type UserStat,
  type ClientStat,
} from '../hooks/useDashboard';
import { useOnboarding, useUpdateOnboarding } from '../hooks/useOnboarding';
import type { OnboardingChecklistItem } from '@pf/shared';

function TrendBadge({ trend }: { trend: TrendStat }) {
  if (trend.pct === null) return null;
  const up = trend.pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium ${
        up ? 'text-green-600' : 'text-red-600'
      }`}
    >
      {up ? '↑' : '↓'} {Math.abs(trend.pct)}% vs last month
    </span>
  );
}

function MetricCard({
  label,
  value,
  sub,
  color = 'text-gray-900',
  trend,
}: {
  label: string;
  value: string | number;
  sub?: React.ReactNode;
  color?: string;
  trend?: TrendStat;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {trend && (
        <div className="mt-1">
          <TrendBadge trend={trend} />
        </div>
      )}
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

const ACTIVITY_ICONS: Record<string, string> = {
  message_sent: '📤',
  reply_received: '💬',
  hit_logged: '⭐',
  contact_added: '➕',
};

const ACTIVITY_COLORS: Record<string, string> = {
  message_sent: 'bg-blue-50 border-blue-100',
  reply_received: 'bg-indigo-50 border-indigo-100',
  hit_logged: 'bg-yellow-50 border-yellow-100',
  contact_added: 'bg-green-50 border-green-100',
};

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ActivityFeed({ items }: { items: ActivityItem[] }) {
  if (items.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-gray-400">
        No activity yet. Start by sending a pitch or logging coverage.
      </div>
    );
  }

  return (
    <ul className="divide-y divide-gray-100">
      {items.map((item) => (
        <li
          key={item.id}
          className={`flex items-start gap-3 px-5 py-4 ${ACTIVITY_COLORS[item.type] ?? ''}`}
        >
          <span className="text-base mt-0.5">{ACTIVITY_ICONS[item.type] ?? '•'}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-800 truncate">{item.description}</p>
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
            {formatRelativeTime(item.timestamp)}
          </span>
        </li>
      ))}
    </ul>
  );
}

type SortDir = 'asc' | 'desc';

function SortIcon({ col, sortCol, sortDir }: { col: string; sortCol: string; sortDir: SortDir }) {
  if (col !== sortCol) return <span className="text-gray-300 ml-1">↕</span>;
  return <span className="text-blue-500 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

function Th({
  label,
  col,
  sortCol,
  sortDir,
  onSort,
}: {
  label: string;
  col: string;
  sortCol: string;
  sortDir: SortDir;
  onSort: (col: string) => void;
}) {
  return (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
      onClick={() => onSort(col)}
    >
      {label}
      <SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
    </th>
  );
}

function sortRows<T extends Record<string, unknown>>(
  rows: T[],
  col: string,
  dir: SortDir,
): T[] {
  return [...rows].sort((a, b) => {
    const av = a[col] as number | string;
    const bv = b[col] as number | string;
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return dir === 'asc' ? cmp : -cmp;
  });
}

function UserBreakdownTable({ rows }: { rows: UserStat[] }) {
  const [sortCol, setSortCol] = useState('messagesSent');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  function handleSort(col: string) {
    if (col === sortCol) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }

  const sorted = sortRows(rows as unknown as Record<string, unknown>[], sortCol, sortDir) as unknown as UserStat[];

  if (rows.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">No team members found.</div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-100">
        <thead className="bg-gray-50">
          <tr>
            <Th label="Name" col="name" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
            <Th label="Messages Sent" col="messagesSent" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
            <Th label="Replies" col="repliesReceived" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
            <Th label="Coverage" col="hitsLogged" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
            <Th label="Active Lists" col="activePressLists" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {sorted.map((u) => (
            <tr key={u.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <p className="text-sm font-medium text-gray-900">{u.name}</p>
                <p className="text-xs text-gray-400">{u.email}</p>
              </td>
              <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">{u.messagesSent.toLocaleString()}</td>
              <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">{u.repliesReceived.toLocaleString()}</td>
              <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">{u.hitsLogged.toLocaleString()}</td>
              <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">{u.activePressLists.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientBreakdownTable({ rows }: { rows: ClientStat[] }) {
  const navigate = useNavigate();
  const [sortCol, setSortCol] = useState('messagesSent');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  function handleSort(col: string) {
    if (col === sortCol) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }

  const sorted = sortRows(rows as unknown as Record<string, unknown>[], sortCol, sortDir) as unknown as ClientStat[];

  if (rows.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">
        No clients yet.{' '}
        <Link to="/clients" className="text-blue-600 hover:underline">
          Add a client →
        </Link>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-100">
        <thead className="bg-gray-50">
          <tr>
            <Th label="Client" col="name" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
            <Th label="Messages Sent" col="messagesSent" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
            <Th label="Reply Rate" col="replyRate" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
            <Th label="Coverage" col="coverageCount" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
            <Th label="Active Lists" col="activePressLists" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {sorted.map((c) => (
            <tr
              key={c.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => navigate(`/clients/${c.id}`)}
            >
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.name}</td>
              <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">{c.messagesSent.toLocaleString()}</td>
              <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">
                <span
                  className={
                    c.replyRate >= 20
                      ? 'text-green-600 font-medium'
                      : c.replyRate > 0
                      ? 'text-yellow-600 font-medium'
                      : 'text-gray-400'
                  }
                >
                  {c.replyRate}%
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">{c.coverageCount.toLocaleString()}</td>
              <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">{c.activePressLists.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CHECKLIST_HREF: Record<string, string> = {
  connect_email: '/outreach',
  import_contacts: '/contacts',
  browse_reporters: '/reporters',
  create_press_list: '/press-lists',
  send_message: '/outreach',
};

function OnboardingChecklist() {
  const { data } = useOnboarding();
  const updateOnboarding = useUpdateOnboarding();
  const navigate = useNavigate();

  if (!data) return null;

  const { checklist, completed_at } = data.data;
  const allDone = checklist.every((item) => item.done);

  if (completed_at || allDone) return null;

  const completedCount = checklist.filter((item) => item.done).length;

  async function handleDismiss() {
    await updateOnboarding.mutateAsync({ completed: true });
  }

  return (
    <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-blue-100 bg-blue-50 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-blue-900">Getting started</h2>
          <p className="text-xs text-blue-600 mt-0.5">
            {completedCount} of {checklist.length} steps complete
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-xs text-blue-400 hover:text-blue-600 transition-colors"
          title="Dismiss checklist"
        >
          Dismiss
        </button>
      </div>
      <ul className="divide-y divide-gray-100">
        {checklist.map((item: OnboardingChecklistItem) => (
          <li key={item.key} className="flex items-center gap-3 px-5 py-3">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                item.done
                  ? 'bg-green-500 border-green-500'
                  : 'border-gray-300'
              }`}
            >
              {item.done && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className={`text-sm flex-1 ${item.done ? 'line-through text-gray-400' : 'text-gray-700'}`}>
              {item.label}
            </span>
            {!item.done && CHECKLIST_HREF[item.key] && (
              <button
                onClick={() => navigate(CHECKLIST_HREF[item.key])}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap transition-colors"
              >
                Go →
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data: onboardingData } = useOnboarding();
  const { data: stats, isLoading, error: statsError } = useDashboardStats();
  const { data: userStats, error: userStatsError } = useDashboardUserStats();
  const { data: clientStats, error: clientStatsError } = useDashboardClientStats();

  // Redirect brand-new users to the onboarding wizard
  React.useEffect(() => {
    if (
      onboardingData &&
      onboardingData.data.step === 0 &&
      onboardingData.data.completed_at === null
    ) {
      navigate('/onboarding', { replace: true });
    }
  }, [onboardingData, navigate]);

  return (
    <div className="p-6 max-w-5xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-0.5">Team-wide activity and performance overview</p>
      </div>

      <OnboardingChecklist />

      {isLoading && (
        <div className="py-12 text-center text-sm text-gray-400">Loading dashboard…</div>
      )}

      {statsError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          Failed to load dashboard stats. Please refresh the page.
        </div>
      )}

      {stats && (
        <>
          {/* Primary metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetricCard
              label="Total Contacts"
              value={stats.totalContacts.toLocaleString()}
              trend={stats.trends.contacts}
            />
            <MetricCard
              label="Press Lists"
              value={stats.totalPressLists.toLocaleString()}
              trend={stats.trends.pressLists}
            />
            <MetricCard
              label="Messages (this month)"
              value={stats.messagesSentThisMonth.toLocaleString()}
              sub={`${stats.messagesSentAllTime.toLocaleString()} all time`}
              trend={stats.trends.messages}
            />
            <MetricCard
              label="Coverage Hits"
              value={stats.totalHits.toLocaleString()}
              trend={stats.trends.hits}
            />
          </div>

          {/* Secondary metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetricCard
              label="Reply Rate"
              value={`${stats.replyRate}%`}
              sub="last 30 days"
              color={
                stats.replyRate >= 20
                  ? 'text-green-600'
                  : stats.replyRate > 0
                  ? 'text-yellow-600'
                  : 'text-gray-900'
              }
            />
            <MetricCard
              label="Bounce Rate"
              value={`${stats.bounceRate}%`}
              sub="last 30 days"
              color={stats.bounceRate > 5 ? 'text-red-600' : 'text-gray-900'}
            />
            <MetricCard
              label="Active Campaigns"
              value={stats.activeCampaigns}
              sub={
                stats.activeCampaigns > 0 ? (
                  <Link to="/press-lists" className="text-blue-600 hover:underline">
                    View press lists →
                  </Link>
                ) : (
                  'No messages sent in 30 days'
                )
              }
            />
            <MetricCard
              label="Total Messages Sent"
              value={stats.messagesSentAllTime.toLocaleString()}
              sub={
                <Link to="/outreach-history" className="text-blue-600 hover:underline">
                  View history →
                </Link>
              }
            />
          </div>

          {/* Recent activity feed */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">Recent Activity</h2>
              <p className="text-xs text-gray-400 mt-0.5">Last 10 actions across the team</p>
            </div>
            <ActivityFeed items={stats.recentActivity} />
          </div>
        </>
      )}

      {/* Team member breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Team Members</h2>
          <p className="text-xs text-gray-400 mt-0.5">Activity breakdown by team member</p>
        </div>
        {userStatsError ? (
          <div className="px-5 py-4 text-sm text-red-600">Failed to load team stats.</div>
        ) : userStats ? (
          <UserBreakdownTable rows={userStats} />
        ) : (
          <div className="py-8 text-center text-sm text-gray-400">Loading…</div>
        )}
      </div>

      {/* Client breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Clients</h2>
          <p className="text-xs text-gray-400 mt-0.5">Activity breakdown by client — click a row to view details</p>
        </div>
        {clientStatsError ? (
          <div className="px-5 py-4 text-sm text-red-600">Failed to load client stats.</div>
        ) : clientStats ? (
          <ClientBreakdownTable rows={clientStats} />
        ) : (
          <div className="py-8 text-center text-sm text-gray-400">Loading…</div>
        )}
      </div>
    </div>
  );
}
